import { useI18n } from "@/api/i18n";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/libs/cn";
import { optionUrlCustomSchema, urlCustomSchema } from "@/libs/zod-errors";
import { useAppForm } from "../../../../create/form-hook";
import { useRefineStore } from "../../store/RefineProvider";

export function ApplicationStep() {
  const { t } = useI18n();
  const { state, updateFields } = useRefineStore();

  const form = useAppForm(() => ({
    defaultValues: {
      apply_url: state.apply_url ?? "",
      calendar_url: state.calendar_url ?? "",
      internal_ats: state.internal_ats ?? true,
    },
    onSubmit: () => {},
  }));

  const syncToStore = () => {
    const values = form.state.values;
    updateFields({
      internal_ats: values.internal_ats,
      apply_url: values.apply_url ?? "",
      calendar_url: values.calendar_url ?? "",
    });
  };

  return (
    <div class="space-y-6">
      <div>
        <h3 class="text-lg font-semibold mb-1">
          {t("jobOffers.create.application.title")}
        </h3>
        <p class="text-sm text-muted-foreground">
          {t("jobOffers.create.application.description")}
        </p>
      </div>

      <form.Subscribe
        selector={(s) => ({
          internal_ats: s.values.internal_ats,
        })}
      >
        {(s) => (
          <div class="flex flex-col gap-3">
            {/* SeVedemo ATS Option */}
            <button
              type="button"
              class={cn(
                "group relative rounded-lg border-2 p-5 text-left w-full transition-colors",
                s().internal_ats
                  ? "border-matisse-500 bg-matisse-50/50"
                  : "border-border hover:border-matisse-300 bg-card",
              )}
              onClick={() => {
                form.setFieldValue("internal_ats", true);
                form.setFieldValue("apply_url", "");
                syncToStore();
              }}
            >
              <div class="flex items-start gap-4">
                <div
                  class={cn(
                    "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                    s().internal_ats
                      ? "border-matisse-500 bg-matisse-500"
                      : "border-gray-300 bg-white",
                  )}
                >
                  {s().internal_ats && (
                    <div class="h-2 w-2 rounded-full bg-white" />
                  )}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-1.5">
                    <span class="font-semibold text-base">
                      {t("jobOffers.create.application.form.sevedemoAts")}
                    </span>
                    <Badge
                      variant="secondary"
                      class="bg-green-100 text-green-700 text-xs"
                    >
                      {t("jobOffers.create.application.form.recommended")}
                    </Badge>
                  </div>
                  <p class="text-sm text-muted-foreground leading-relaxed">
                    {t(
                      "jobOffers.create.application.form.sevedemoAtsDescription",
                    )}
                  </p>
                  {s().internal_ats && (
                    <form.AppField
                      name="calendar_url"
                      validators={{
                        onBlur: ({ value }) => {
                          const parsed = optionUrlCustomSchema.safeParse(value);
                          if (!parsed.success) {
                            return parsed.error.issues[0].message;
                          }
                          syncToStore();
                        },
                      }}
                    >
                      {(field) => (
                        <div class="mt-4">
                          <field.TextField
                            label={t(
                              "jobOffers.create.application.form.calendarUrl",
                            )}
                            placeholder={t(
                              "jobOffers.create.application.form.calendarUrlPlaceholder",
                            )}
                            helpText={t(
                              "jobOffers.create.application.form.calendarUrlDescription",
                            )}
                          />
                        </div>
                      )}
                    </form.AppField>
                  )}
                </div>
              </div>
            </button>

            {/* Custom ATS Option */}
            <button
              type="button"
              class={cn(
                "group relative rounded-lg border-2 p-5 text-left w-full transition-colors",
                !s().internal_ats
                  ? "border-matisse-500 bg-matisse-50/50"
                  : "border-border hover:border-matisse-300 bg-card",
              )}
              onClick={() => {
                form.setFieldValue("internal_ats", false);
                form.setFieldValue("calendar_url", "");
                syncToStore();
              }}
            >
              <div class="flex items-start gap-4">
                <div
                  class={cn(
                    "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
                    !s().internal_ats
                      ? "border-matisse-500 bg-matisse-500"
                      : "border-gray-300 bg-white",
                  )}
                >
                  {!s().internal_ats && (
                    <div class="h-2 w-2 rounded-full bg-white" />
                  )}
                </div>
                <div class="flex-1 min-w-0">
                  <span class="font-semibold text-base block mb-1.5">
                    {t("jobOffers.create.application.form.customAts")}
                  </span>
                  <p class="text-sm text-muted-foreground leading-relaxed">
                    {t(
                      "jobOffers.create.application.form.customAtsDescription",
                    )}
                  </p>
                  {!s().internal_ats && (
                    <form.AppField
                      name="apply_url"
                      validators={{
                        onBlur: ({ value }) => {
                          const parsed = urlCustomSchema.safeParse(value);
                          if (!parsed.success) {
                            return parsed.error.issues[0].message;
                          }
                          syncToStore();
                        },
                      }}
                    >
                      {(field) => (
                        <div class="mt-4">
                          <field.TextField
                            label={`${t("jobOffers.create.application.form.applyUrl")} *`}
                            placeholder={t(
                              "jobOffers.create.application.form.applyUrlPlaceholder",
                            )}
                            helpText={t(
                              "jobOffers.create.application.form.applyUrlDescription",
                            )}
                          />
                        </div>
                      )}
                    </form.AppField>
                  )}
                </div>
              </div>
            </button>
          </div>
        )}
      </form.Subscribe>
    </div>
  );
}
